/**
 * Rally-level Match Intelligence from live scoring `rallyHistory`.
 * Compact summaries for room history + matchRecords (no full undo payload).
 */

/**
 * @param {object|null|undefined} scoringMatch finished match engine state
 * @param {{ name?: string, slots?: Array<{ playerId?: string, displayName?: string, registryEntryId?: string }> }|null} [teamA]
 * @param {{ name?: string, slots?: Array<{ playerId?: string, displayName?: string, registryEntryId?: string }> }|null} [teamB]
 */
export function extractRallySummary(scoringMatch, teamA = null, teamB = null) {
  const games = Array.isArray(scoringMatch?.games) ? scoringMatch.games : [];
  if (!games.length) return null;

  const nameById = buildPlayerNameMap(teamA, teamB);
  /** @type {Array<{ index: number, winner: string|null, points: string[], servers: string[], serverIds: (string|null)[] }>} */
  const outGames = [];
  let bestStreak = 0;
  /** @type {{ length: number, side: string, playerId: string|null, playerName: string|null, gameIndex: number }|null} */
  let longestServe = null;
  let totalRallies = 0;

  games.forEach((game, gameIndex) => {
    const history = Array.isArray(game?.rallyHistory) ? game.rallyHistory : [];
    const points = [];
    const servers = [];
    const serverIds = [];
    let run = 0;
    let runSide = null;
    let runPlayerId = null;

    history.forEach((entry) => {
      const winningSide = entry?.winningSide === "B" ? "B" : "A";
      const servingSide = entry?.prevServingSide === "B" ? "B" : "A";
      const serverId = entry?.prevServingPlayerId || null;
      points.push(winningSide);
      servers.push(servingSide);
      serverIds.push(serverId);
      totalRallies += 1;

      // Serve held when the server wins the rally.
      if (winningSide === servingSide) {
        if (runSide === servingSide && runPlayerId === serverId) {
          run += 1;
        } else {
          run = 1;
          runSide = servingSide;
          runPlayerId = serverId;
        }
        if (run > bestStreak) {
          bestStreak = run;
          longestServe = {
            length: run,
            side: servingSide,
            playerId: serverId,
            playerName: serverId ? nameById.get(serverId) || null : null,
            gameIndex,
          };
        }
      } else {
        run = 0;
        runSide = null;
        runPlayerId = null;
      }
    });

    // Reconstruct final scores if missing
    let scoreA = Number(game?.sideA?.score);
    let scoreB = Number(game?.sideB?.score);
    if (!Number.isFinite(scoreA) || !Number.isFinite(scoreB)) {
      scoreA = points.filter((p) => p === "A").length;
      scoreB = points.filter((p) => p === "B").length;
    }

    outGames.push({
      index: gameIndex,
      winner: game?.gameWinner === "B" ? "B" : game?.gameWinner === "A" ? "A" : null,
      scoreA,
      scoreB,
      points,
      servers,
      serverIds,
    });
  });

  if (!totalRallies) return null;

  // Side label fallback when player name unknown
  if (longestServe && !longestServe.playerName) {
    longestServe.playerName =
      longestServe.side === "A"
        ? teamA?.name || "Team A"
        : teamB?.name || "Team B";
  }

  return {
    version: 1,
    totalRallies,
    games: outGames,
    longestServeStreak: longestServe,
  };
}

/**
 * Point-flow series for visualization (match-level cumulative + per-game beads).
 * @param {object|null} rallySummary
 * @param {object} [insights] set-level fallback when no rally data
 */
export function buildPointFlowModel(rallySummary, insights = null) {
  if (rallySummary?.games?.length) {
    let cumA = 0;
    let cumB = 0;
    /** @type {Array<{ i: number, game: number, winner: string, serving: string, cumA: number, cumB: number, label: string }>} */
    const sequence = [];
    rallySummary.games.forEach((g) => {
      (g.points || []).forEach((winner, pi) => {
        if (winner === "A") cumA += 1;
        else cumB += 1;
        sequence.push({
          i: sequence.length,
          game: g.index + 1,
          winner,
          serving: g.servers?.[pi] || winner,
          cumA,
          cumB,
          label: `G${g.index + 1} · ${cumA}–${cumB}`,
        });
      });
    });
    return {
      mode: "rally",
      sequence,
      games: rallySummary.games.map((g) => ({
        index: g.index,
        label: `Game ${g.index + 1}`,
        points: g.points || [],
        scoreA: g.scoreA,
        scoreB: g.scoreB,
        winner: g.winner,
      })),
      longestServeStreak: rallySummary.longestServeStreak || null,
      totalRallies: rallySummary.totalRallies || sequence.length,
    };
  }

  // Fallback: game endpoints only
  const games = insights?.games || [];
  if (!games.length) return null;
  let cumA = 0;
  let cumB = 0;
  const sequence = [{ i: 0, game: 0, winner: null, serving: null, cumA: 0, cumB: 0, label: "Start" }];
  games.forEach((g) => {
    cumA += Number(g.scoreA) || 0;
    cumB += Number(g.scoreB) || 0;
    sequence.push({
      i: sequence.length,
      game: g.index + 1,
      winner: g.winner,
      serving: null,
      cumA,
      cumB,
      label: `G${g.index + 1}`,
    });
  });
  return {
    mode: "games",
    sequence,
    games: games.map((g) => ({
      index: g.index,
      label: g.label,
      points: [],
      scoreA: g.scoreA,
      scoreB: g.scoreB,
      winner: g.winner,
    })),
    longestServeStreak: null,
    totalRallies: null,
  };
}

function buildPlayerNameMap(teamA, teamB) {
  /** @type {Map<string, string>} */
  const map = new Map();
  const ingest = (team) => {
    (team?.slots || []).forEach((s) => {
      const name = s?.displayName;
      if (!name) return;
      if (s.registryEntryId) map.set(s.registryEntryId, name);
      if (s.playerId) map.set(s.playerId, name);
      // Engine player ids often use registryEntryId
    });
  };
  ingest(teamA);
  ingest(teamB);
  return map;
}
